import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, pipelineApi, formatApiError } from '../lib/api';
import usePageTitle from '../hooks/usePageTitle';
import { SettingsNav } from './CognitiveProfilePage';
import {
  Loader2, Plus, X, Shield, ShieldCheck, KeyRound, Trash2, Pencil,
  UserPlus, Users, CircleSlash, CheckCircle2,
  Database, PlayCircle, History, AlertTriangle, PoundSterling, RefreshCw,
} from 'lucide-react';

const fmtGBP = (n) => `£${Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-GB'); } catch { return iso; } };

const inputCls = "w-full p-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 placeholder:text-[#A3B5C7]/40";
const labelCls = "block text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 font-medium";
const ROLE_OPTIONS = ['CEO', 'CTO', 'Chief of Staff', 'Head of Partnerships', 'Admin', 'Viewer'];
const DEFAULT_ROLE = 'CTO';

const Toggle = ({ checked, onChange, label, testId, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    data-testid={testId}
    className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-[#00D4C8]' : 'bg-[#1A2F4C]'}`}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
    </span>
    <span className="text-[#D4E2F0]">{label}</span>
  </button>
);

const Modal = ({ title, children, onClose, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid={testId}>
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-md rounded-xl bg-[#0E1C33] border border-[#1A2F4C] p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">{title}</h3>
        <button onClick={onClose} data-testid="modal-close-btn" className="text-[#A3B5C7] hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </motion.div>
  </div>
);

export default function AdminPage() {
  usePageTitle('Admin');
  const { isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', role: DEFAULT_ROLE, password: '', is_admin: false });

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', is_admin: false, is_active: true });

  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState('');

  const [deleteUser, setDeleteUser] = useState(null);

  // Data-integrity tools (admin-only).
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const runDiagnostic = async () => {
    setDiagLoading(true);
    try {
      const res = await pipelineApi.dealValueDiagnostic();
      setDiag(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setDiagLoading(false); }
  };

  const runCleanup = async () => {
    setCleanupBusy(true);
    try {
      const res = await pipelineApi.cleanup();
      setCleanupResult(res.data);
      setCleanupConfirm(false);
      toast.success('Pipeline cleanup complete');
      if (history !== null) loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCleanupBusy(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await pipelineApi.cleanupHistory();
      setHistory(res.data.runs || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setHistoryLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listUsers();
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <SettingsNav />
        <div className="rounded-xl bg-[#0E1C33] border border-red-500/30 p-8 text-center" data-testid="admin-forbidden">
          <Shield className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-white font-medium">Admin access required</p>
          <p className="text-[#A3B5C7] text-sm mt-1">You do not have permission to manage users.</p>
        </div>
      </div>
    );
  }

  const handleAdd = async () => {
    setBusy(true);
    try {
      await adminApi.createUser(addForm);
      toast.success(`${addForm.name || addForm.email} added`);
      setAddOpen(false);
      setAddForm({ name: '', email: '', role: DEFAULT_ROLE, password: '', is_admin: false });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, is_admin: u.is_admin_flag, is_active: u.is_active });
  };

  const handleEdit = async () => {
    setBusy(true);
    try {
      await adminApi.updateUser(editUser.id, editForm);
      toast.success('User updated');
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const handleReset = async () => {
    setBusy(true);
    try {
      await adminApi.resetPassword(resetUser.id, resetPw);
      toast.success(`Password reset for ${resetUser.name || resetUser.email}`);
      setResetUser(null);
      setResetPw('');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await adminApi.deleteUser(deleteUser.id);
      toast.success(`${deleteUser.name || deleteUser.email} deleted`);
      setDeleteUser(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-page">
      <SettingsNav />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users className="w-6 h-6 text-[#00D4C8]" /> User Management</h1>
          <p className="text-[#A3B5C7] text-sm mt-1">Add, edit, and manage team access. Admin-only.</p>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-user-btn" className="btn-primary flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Add User
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#A3B5C7] py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading users...</div>
      ) : (
        <div className="rounded-xl bg-[#0E1C33] border border-[#1A2F4C] overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1A2F4C] text-[10px] uppercase tracking-wider text-[#A3B5C7]">
                <th className="text-left py-3 px-4 font-medium">Name</th>
                <th className="text-left py-3 px-4 font-medium">Email</th>
                <th className="text-left py-3 px-4 font-medium">Role</th>
                <th className="text-left py-3 px-4 font-medium">Admin</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-right py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1A2F4C]/50 hover:bg-[#0A1628]/50" data-testid={`user-row-${u.email}`}>
                  <td className="py-3 px-4 text-white font-medium">{u.name || '—'}</td>
                  <td className="py-3 px-4 text-[#D4E2F0]">{u.email}</td>
                  <td className="py-3 px-4 text-[#A3B5C7]">{u.role}</td>
                  <td className="py-3 px-4">
                    {u.is_admin ? (
                      <span className="inline-flex items-center gap-1 text-[#00D4C8] text-xs" title={u.is_bootstrap_admin ? 'Bootstrap admin (by email)' : 'Admin'}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Admin{u.is_bootstrap_admin ? ' (bootstrap)' : ''}
                      </span>
                    ) : <span className="text-[#A3B5C7]/50 text-xs">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    {u.is_active
                      ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Active</span>
                      : <span className="inline-flex items-center gap-1 text-amber-400 text-xs"><CircleSlash className="w-3.5 h-3.5" /> Deactivated</span>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} className="text-[#A3B5C7] hover:text-[#00D4C8]" title="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => { setResetUser(u); setResetPw(''); }} data-testid={`reset-user-${u.email}`} className="text-[#A3B5C7] hover:text-[#00D4C8]" title="Reset password"><KeyRound className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.email}`} className="text-[#A3B5C7] hover:text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Data Integrity (admin-only) ---- */}
      <div className="mt-8" data-testid="data-integrity-panel">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-5 h-5 text-[#00D4C8]" />
          <h2 className="text-lg font-semibold text-white">Data Integrity</h2>
        </div>
        <p className="text-[#A3B5C7] text-sm mb-4">Inspect and repair pipeline data. Recommended order: run the read-only diagnostic first, review, then run cleanup.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <button onClick={runDiagnostic} disabled={diagLoading} data-testid="run-diagnostic-btn"
            className="flex items-center gap-3 rounded-xl bg-[#0E1C33] border border-[#1A2F4C] hover:border-[#00D4C8]/40 p-4 text-left transition-colors disabled:opacity-50">
            {diagLoading ? <Loader2 className="w-5 h-5 animate-spin text-[#00D4C8] shrink-0" /> : <PoundSterling className="w-5 h-5 text-[#00D4C8] shrink-0" />}
            <div><div className="text-white text-sm font-medium">Deal value diagnostic</div><div className="text-[10px] text-[#A3B5C7]">Read-only — never mutates data</div></div>
          </button>

          <button onClick={() => { setCleanupResult(null); setCleanupConfirm(true); }} disabled={cleanupBusy} data-testid="run-cleanup-btn"
            className="flex items-center gap-3 rounded-xl bg-[#0E1C33] border border-amber-500/30 hover:border-amber-400/60 p-4 text-left transition-colors disabled:opacity-50">
            {cleanupBusy ? <Loader2 className="w-5 h-5 animate-spin text-amber-400 shrink-0" /> : <PlayCircle className="w-5 h-5 text-amber-400 shrink-0" />}
            <div><div className="text-white text-sm font-medium">Run pipeline cleanup</div><div className="text-[10px] text-[#A3B5C7]">Deletes orphans/dupes · confirms first</div></div>
          </button>

          <button onClick={loadHistory} disabled={historyLoading} data-testid="cleanup-history-btn"
            className="flex items-center gap-3 rounded-xl bg-[#0E1C33] border border-[#1A2F4C] hover:border-[#00D4C8]/40 p-4 text-left transition-colors disabled:opacity-50">
            {historyLoading ? <Loader2 className="w-5 h-5 animate-spin text-[#00D4C8] shrink-0" /> : <History className="w-5 h-5 text-[#00D4C8] shrink-0" />}
            <div><div className="text-white text-sm font-medium">Cleanup history</div><div className="text-[10px] text-[#A3B5C7]">Audit trail of past runs</div></div>
          </button>
        </div>

        {/* Latest cleanup result */}
        {cleanupResult && (
          <div className="rounded-xl bg-[#0E1C33] border border-emerald-500/30 p-4 mb-4" data-testid="cleanup-result">
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-white text-sm font-medium">Cleanup complete</span><span className="text-[10px] text-[#A3B5C7] font-mono">run {cleanupResult.run_id}</span></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div className="text-[#A3B5C7]">Orphans deleted: <span className="text-white">{cleanupResult.orphans_deleted}</span></div>
              <div className="text-[#A3B5C7]">Orphan value removed: <span className="text-white">{fmtGBP(cleanupResult.orphan_value_removed)}</span></div>
              <div className="text-[#A3B5C7]">Duplicates deleted: <span className="text-white">{cleanupResult.duplicates_deleted}</span></div>
              <div className="text-[#A3B5C7]">Owners canonicalised: <span className="text-white">{cleanupResult.owner_canonicalised}</span></div>
              <div className="text-[#A3B5C7]">Non-canonical left: <span className="text-white">{(cleanupResult.non_canonical_owners || []).length}</span></div>
            </div>
          </div>
        )}

        {/* Diagnostic result */}
        {diag && (
          <div className="rounded-xl bg-[#0E1C33] border border-[#1A2F4C] p-4 mb-4" data-testid="diagnostic-result">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white text-sm font-medium">Deal value distribution</span>
              <button onClick={runDiagnostic} className="text-[#A3B5C7] hover:text-[#00D4C8]" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-4">
              <div className="rounded-lg bg-[#0A1628] p-2"><div className="text-[#A3B5C7]">Records</div><div className="text-white text-base font-semibold">{diag.total_records}</div></div>
              <div className="rounded-lg bg-[#0A1628] p-2"><div className="text-[#A3B5C7]">With value</div><div className="text-white text-base font-semibold">{diag.records_with_value}</div></div>
              <div className="rounded-lg bg-[#0A1628] p-2"><div className="text-[#A3B5C7]">Total value</div><div className="text-white text-base font-semibold">{fmtGBP(diag.total_deal_value)}</div></div>
              <div className="rounded-lg bg-[#0A1628] p-2"><div className="text-[#A3B5C7]">Avg (non-zero)</div><div className="text-white text-base font-semibold">{fmtGBP(diag.average_non_zero)}</div></div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1">Largest {(diag.largest_ten || []).length} records</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="diagnostic-largest-table">
                <thead><tr className="text-[10px] uppercase text-[#A3B5C7] border-b border-[#1A2F4C]">
                  <th className="text-left py-2 pr-3">Target</th><th className="text-right py-2 px-3">Value</th><th className="text-left py-2 px-3">Stage</th><th className="text-left py-2 px-3">Owner</th><th className="text-left py-2 px-3">Created</th><th className="text-left py-2 pl-3">By</th>
                </tr></thead>
                <tbody>
                  {(diag.largest_ten || []).map((r) => (
                    <tr key={r.pipeline_record_id} className="border-b border-[#1A2F4C]/40">
                      <td className="py-2 pr-3 text-white">{r.target_name}</td>
                      <td className="py-2 px-3 text-right text-[#00D4C8]">{fmtGBP(r.deal_value)}</td>
                      <td className="py-2 px-3 text-[#A3B5C7]">{r.stage}</td>
                      <td className="py-2 px-3 text-[#A3B5C7]">{r.owner}</td>
                      <td className="py-2 px-3 text-[#A3B5C7]">{fmtDate(r.created_at)}</td>
                      <td className="py-2 pl-3 text-[#A3B5C7]">{r.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* History */}
        {history && (
          <div className="rounded-xl bg-[#0E1C33] border border-[#1A2F4C] p-4" data-testid="cleanup-history-panel">
            <span className="text-white text-sm font-medium">Cleanup history ({history.length})</span>
            {history.length === 0 ? (
              <p className="text-[#A3B5C7] text-xs mt-2">No cleanup runs recorded yet.</p>
            ) : (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase text-[#A3B5C7] border-b border-[#1A2F4C]">
                    <th className="text-left py-2 pr-3">When</th><th className="text-left py-2 px-3">By</th><th className="text-right py-2 px-3">Orphans</th><th className="text-right py-2 px-3">Dupes</th><th className="text-right py-2 pl-3">Canonicalised</th>
                  </tr></thead>
                  <tbody>
                    {history.map((r) => (
                      <tr key={r.id} className="border-b border-[#1A2F4C]/40" data-testid={`cleanup-run-${r.id}`}>
                        <td className="py-2 pr-3 text-[#D4E2F0]">{fmtDate(r.ran_at)}</td>
                        <td className="py-2 px-3 text-[#A3B5C7]">{r.ran_by_email || r.ran_by_name || '—'}</td>
                        <td className="py-2 px-3 text-right text-white">{r.orphans_deleted}</td>
                        <td className="py-2 px-3 text-right text-white">{r.duplicates_deleted}</td>
                        <td className="py-2 pl-3 text-right text-white">{r.owner_canonicalised}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cleanup confirmation */}
      {cleanupConfirm && (
        <Modal title="Run pipeline cleanup?" onClose={() => setCleanupConfirm(false)} testId="cleanup-confirm-modal">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[#D4E2F0] text-sm">This will permanently <span className="text-white">delete orphaned and duplicate pipeline records</span> and canonicalise all owner names across targets and pipeline. It is idempotent and every run is recorded in the audit trail. Continue?</p>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setCleanupConfirm(false)} className="btn-secondary" data-testid="cleanup-cancel-btn">Cancel</button>
            <button onClick={runCleanup} disabled={cleanupBusy} data-testid="cleanup-confirm-btn" className="px-4 py-2 rounded-lg bg-amber-500/90 hover:bg-amber-500 text-white text-sm flex items-center gap-2">{cleanupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Run cleanup</button>
          </div>
        </Modal>
      )}

      {addOpen && (
        <Modal title="Add User" onClose={() => setAddOpen(false)} testId="add-user-modal">
          <div className="space-y-3">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-name-input" /></div>
            <div><label className={labelCls}>Email</label><input className={inputCls} value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="add-email-input" /></div>
            <div>
              <label className={labelCls}>Role</label>
              <select className={inputCls} value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })} data-testid="add-role-input">
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Password</label><input type="text" className={inputCls} value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} data-testid="add-password-input" placeholder="min 8 characters" /></div>
            <Toggle checked={addForm.is_admin} onChange={(v) => setAddForm({ ...addForm, is_admin: v })} label="Admin access" testId="add-admin-toggle" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setAddOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleAdd} disabled={busy || !addForm.email || !addForm.password} data-testid="add-user-submit" className="btn-primary flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit user */}
      {editUser && (
        <Modal title={`Edit ${editUser.name || editUser.email}`} onClose={() => setEditUser(null)} testId="edit-user-modal">
          <div className="space-y-3">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name-input" /></div>
            <div className="text-xs text-[#A3B5C7]">Email: {editUser.email}</div>
            <div>
              <label className={labelCls}>Role</label>
              <select className={inputCls} value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} data-testid="edit-role-input">
                {(ROLE_OPTIONS.includes(editForm.role) ? ROLE_OPTIONS : [editForm.role, ...ROLE_OPTIONS]).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Toggle checked={editForm.is_admin} onChange={(v) => setEditForm({ ...editForm, is_admin: v })} label="Admin access" testId="edit-admin-toggle" disabled={editUser.is_bootstrap_admin} />
            {editUser.is_bootstrap_admin && <p className="text-[10px] text-[#A3B5C7]/70">This account is a bootstrap admin (by email) and always retains admin access.</p>}
            <Toggle checked={editForm.is_active} onChange={(v) => setEditForm({ ...editForm, is_active: v })} label="Active" testId="edit-active-toggle" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditUser(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleEdit} disabled={busy} data-testid="edit-user-submit" className="btn-primary flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset password */}
      {resetUser && (
        <Modal title={`Reset password — ${resetUser.name || resetUser.email}`} onClose={() => setResetUser(null)} testId="reset-password-modal">
          <div className="space-y-3">
            <div><label className={labelCls}>New password</label><input type="text" className={inputCls} value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="reset-password-input" placeholder="min 8 characters" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setResetUser(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleReset} disabled={busy || resetPw.length < 8} data-testid="reset-password-submit" className="btn-primary flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteUser && (
        <Modal title="Delete user" onClose={() => setDeleteUser(null)} testId="delete-user-modal">
          <p className="text-[#D4E2F0] text-sm">Are you sure you want to permanently delete <span className="text-white font-medium">{deleteUser.name || deleteUser.email}</span>? This cannot be undone.</p>
          <div className="flex justify-end gap-2 pt-4">
            <button onClick={() => setDeleteUser(null)} className="btn-secondary">Cancel</button>
            <button onClick={handleDelete} disabled={busy} data-testid="delete-user-confirm" className="px-4 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-sm flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
